/**
 * Routing.
 *
 * Two route families with different gates: public pages render for anyone, and
 * portal pages require a Firebase session. The gate is a real component rather than
 * a conditional inside each page, so a new portal page cannot forget to check.
 */

import { Suspense, lazy, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'

import { useSession } from './providers'
import { capturePageview } from '@/shared/lib/analytics'
import { titleClaimedFor } from '@/shared/lib/pageTitle'
import { Loading } from '@/shared/ui/primitives'

import { AcceptInvitation } from '@/pages/AcceptInvitation'
import { Account } from '@/pages/Account'
import { Activate } from '@/pages/Activate'
import { Authorize } from '@/pages/Authorize'
import { Audit } from '@/pages/Audit'
import { Billing } from '@/pages/Billing'
import { Devices } from '@/pages/Devices'
import { MachineTokens } from '@/pages/MachineTokens'
import { Downloads } from '@/pages/Downloads'
import { Enterprise } from '@/pages/Enterprise'
import { Examples } from '@/pages/Examples'
import { HowItWorks } from '@/pages/HowItWorks'
import { Landing } from '@/pages/Landing'
import { Privacy, Terms } from '@/pages/Legal'
import { NotFound } from '@/pages/NotFound'
import { OfflineLicenses } from '@/pages/OfflineLicenses'
import { Pricing } from '@/pages/Pricing'
import { Seats } from '@/pages/Seats'
import { SignIn } from '@/pages/SignIn'
import { NewTicket, Support } from '@/pages/Support'
import { SupportTicket } from '@/pages/SupportTicket'
import { Team } from '@/pages/Team'
import { TeamEnterprise } from '@/pages/TeamEnterprise'
import { TeamUsage } from '@/pages/TeamUsage'
import { Whitepaper } from '@/pages/Whitepaper'

/**
 * The documentation, in its own chunk.
 *
 * It is nine pages and a generated command reference, and a visitor who reads the
 * landing and leaves should not download any of it. See pages/docs/section.tsx.
 */
const DocsSection = lazy(() => import('@/pages/docs/section'))

/**
 * The changelog, in its own chunk.
 *
 * The page itself is lazy because a visitor who reads the landing and leaves should
 * not download it: statically imported it rode in the entry chunk, and that is what
 * moved the bundle guard three times (#260). The release notes are no longer in this
 * chunk either — `entities/releases` loads one version at a time, so the fifty-two
 * versions of notes are fifty-two chunks and a reader gets the one they opened
 * (#487). `/changelog/:version` is the same page; the version is which notes.
 */
const Changelog = lazy(() => import('@/pages/Changelog'))

/**
 * The federation views, in their own chunk: a Team or Enterprise page few accounts
 * open, and the visitors who never sign in should not download it.
 */
const Federation = lazy(() => import('@/pages/Federation'))

/**
 * The CAVS DB Server page, in its own chunk: an Enterprise-only page few accounts
 * open, and visitors who never sign in should not download it.
 */
const CavsdbServer = lazy(() => import('@/pages/CavsdbServer'))

/**
 * The policy editor, in its own chunk: an admin page few accounts open, and it grew
 * by the read-policy editor (#1164) to the point where riding in the entry chunk put
 * every visitor within a few kilobytes of the initial budget (#1313).
 */
const Policies = lazy(() => import('@/pages/Policies').then((m) => ({ default: m.Policies })))

/** /verify reuses the docs kit, so it loads with its own chunk rather than the landing's. */
const Verify = lazy(() => import('@/pages/Verify'))

function RequireSession({ children }: { children: ReactNode }) {
  const { user, ready } = useSession()
  const location = useLocation()

  if (!ready) {
    return <Loading label="Checking your session" />
  }
  if (!user) {
    // Preserve where they were going, so signing in lands them there and not on a
    // generic dashboard.
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/signin?next=${next}`} replace />
  }
  return <>{children}</>
}

/**
 * Scrolls to `#fragment` after a client-side navigation.
 *
 * The browser only honours a fragment on a real document load. Every in-app link to
 * one — `/#benchmark` from the masthead, `/privacy#telemetry` from the footer and the
 * landing's privacy section — changed the URL and left the reader at the top of the
 * page, which reads as a broken link.
 *
 * Runs on a rAF because the target usually does not exist yet at the moment the route
 * commits, and falls back to the top of the page when there is no fragment, which is
 * what a new page should do anyway.
 */
function ScrollToHash() {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 })
      return
    }
    const frame = requestAnimationFrame(() => {
      // decodeURIComponent, because a fragment can legitimately be percent-encoded
      // and querySelector would then match nothing.
      const target = document.getElementById(decodeURIComponent(hash.slice(1)))
      target?.scrollIntoView({ block: 'start' })
    })
    return () => cancelAnimationFrame(frame)
  }, [pathname, hash])

  return null
}

/**
 * SPA pageviews for PostHog. Autocapture pageviews are off because prerender and
 * client hydrate would otherwise double-count the first route.
 */
function CapturePageviews() {
  const { pathname } = useLocation()

  useEffect(() => {
    let current = true
    // The prerender writes each route's title and description into its HTML, but a
    // client-side navigation keeps whatever the first page had. Applying the same
    // meta here, before the pageview, keeps the tab and PostHog's $title honest.
    // Loaded lazily: routes.ts pulls in the CLI reference and release notes, which
    // the main chunk must not carry.
    import('@/routes')
      .then(async ({ getRouteMeta, FALLBACK_META }) => ({
        meta: await getRouteMeta(pathname),
        fallback: FALLBACK_META,
      }))
      .then(({ meta, fallback }) => {
        if (!current) return
        // A portal page or the 404 names itself (usePageTitle); the generic
        // fallback must not overwrite it.
        if (meta === fallback && titleClaimedFor(pathname)) return
        document.title = meta.title
        const description = document.querySelector('meta[name="description"]')
        if (description && meta.description) description.setAttribute('content', meta.description)
      })
      .catch(() => {})
      .finally(() => capturePageview(pathname))
    return () => {
      current = false
    }
  }, [pathname])

  return null
}

export function AppRoutes() {
  return (
    <>
      <ScrollToHash />
      <CapturePageviews />
      <InnerRoutes />
    </>
  )
}

function InnerRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      {/* The three interactive diagrams /how-it-works used to stack after its own
          example. A separate URL rather than a tab, so it can be linked to and sent. */}
      <Route path="/examples" element={<Examples />} />
      <Route
        path="/verify"
        element={
          <Suspense fallback={<Loading label="Loading" />}>
            <Verify />
          </Suspense>
        }
      />
      <Route
        path="/changelog"
        element={
          <Suspense fallback={<Loading label="Loading the changelog" />}>
            <Changelog />
          </Suspense>
        }
      />
      {/* One version per URL, so a release notice can link to the notes it is about
          and the reader downloads those notes and no others. The page redirects the
          `#v0.41.0` fragments the single-page changelog handed out. */}
      <Route
        path="/changelog/:version"
        element={
          <Suspense fallback={<Loading label="Loading the changelog" />}>
            <Changelog />
          </Suspense>
        }
      />

      {/* Documentation. Public on purpose: a developer tool is evaluated by its
          documentation before it is installed, and every word of the reference already
          ships inside `chi --help`. What may and may not be said there is
          archis-web/docs/PUBLIC_DOCS_POLICY.md.

          One route with a wildcard, because the section owns its own sub-routes and
          arrives as a single lazy chunk. */}
      <Route
        path="/docs/*"
        element={
          <Suspense fallback={<Loading label="Loading the documentation" />}>
            <DocsSection />
          </Suspense>
        }
      />

      <Route path="/enterprise" element={<Enterprise />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/whitepaper" element={<Whitepaper />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      {/* Public: downloading the Free CLI needs no account (#404). The account comes in
          at `chi auth login`; the page picks the portal frame only when signed in. */}
      <Route path="/downloads" element={<Downloads />} />

      {/* Pilot routes redirected */}
      <Route path="/pilot" element={<Navigate to="/pricing" replace />} />
      <Route path="/pilot/redeem" element={<Navigate to="/pricing" replace />} />
      {/* Activation handles its own signed-out state so the verification code
          survives the sign-in round trip. */}
      {/* The default `chi auth login` path: the link carries a session id, nothing is typed. */}
      <Route path="/cli/authorize" element={<Authorize />} />
      {/* Kept for `chi auth login --no-browser`, which is the only way in on a machine with no
          browser — an SSH session, a container, a devcontainer. Removing it would make
          those machines impossible to sign in from. */}
      <Route path="/cli/activate" element={<Activate />} />
      <Route path="/invitations/:token" element={<AcceptInvitation />} />

      {/* Portal */}
      <Route
        path="/account"
        element={
          <RequireSession>
            <Account />
          </RequireSession>
        }
      />
      <Route
        path="/billing"
        element={
          <RequireSession>
            <Billing />
          </RequireSession>
        }
      />
      {/* Stripe's return URL, kept distinct from /billing so the "processing" notice
          only shows on an actual return. */}
      <Route
        path="/billing/return"
        element={
          <RequireSession>
            <Billing />
          </RequireSession>
        }
      />
      <Route
        path="/devices"
        element={
          <RequireSession>
            <Devices />
          </RequireSession>
        }
      />
      <Route
        path="/machine-tokens"
        element={
          <RequireSession>
            <MachineTokens />
          </RequireSession>
        }
      />
      {/* Support. `/support/new` is declared before `/support/:id` so the literal
          wins — otherwise "new" is matched as a ticket id and the form is a 404. */}
      <Route
        path="/support"
        element={
          <RequireSession>
            <Support />
          </RequireSession>
        }
      />
      <Route
        path="/support/new"
        element={
          <RequireSession>
            <NewTicket />
          </RequireSession>
        }
      />
      <Route
        path="/support/:id"
        element={
          <RequireSession>
            <SupportTicket />
          </RequireSession>
        }
      />
      <Route
        path="/team"
        element={
          <RequireSession>
            <Team />
          </RequireSession>
        }
      />
      <Route
        path="/team/seats"
        element={
          <RequireSession>
            <Seats />
          </RequireSession>
        }
      />
      <Route
        path="/team/policies"
        element={
          <RequireSession>
            <Suspense fallback={<Loading label="Loading" />}>
              <Policies />
            </Suspense>
          </RequireSession>
        }
      />
      <Route
        path="/team/offline-licences"
        element={
          <RequireSession>
            <OfflineLicenses />
          </RequireSession>
        }
      />
      <Route
        path="/team/cavsdb"
        element={
          <RequireSession>
            <Suspense fallback={<Loading label="Loading" />}>
              <CavsdbServer />
            </Suspense>
          </RequireSession>
        }
      />
      <Route
        path="/team/usage"
        element={
          <RequireSession>
            <TeamUsage />
          </RequireSession>
        }
      />
      <Route
        path="/team/enterprise"
        element={
          <RequireSession>
            <TeamEnterprise />
          </RequireSession>
        }
      />
      <Route
        path="/team/audit"
        element={
          <RequireSession>
            <Audit />
          </RequireSession>
        }
      />
      <Route
        path="/team/federation"
        element={
          <RequireSession>
            <Suspense fallback={<Loading label="Loading" />}>
              <Federation />
            </Suspense>
          </RequireSession>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
